import { addDays, isSettingLookCode, type SettingLookCode } from '@gk/contracts'
import { localDate } from '../lib/time.ts'
import { lookForChoice, type JobAreaRow, type JobRow, type TaskTypeRow } from './plan.ts'
import type { SettingsConfirmationRow } from './settingsDone.ts'
import type { CoverageRow, JobCompletionRow } from './work.ts'

/** M25-A (docs/M25.md D, "The history", rules 2 and 4; ADR-131): THE CUTS THE SETTINGS RULE READS, as one pure builder. No I/O, no clock, no randomness: days come
 *  in as arguments (`today`, `until`) and are cut on the site's days (`lib/time.ts`), never on UTC.
 *  - **done** lines come from the COMPLETIONS and the areas their COVERAGE names (full or partial), exactly as `areaHistory` reads them, never from the job's
 *    state: a job still `in_progress` because another session is open, or resumed after a partial finish, keeps its finished sessions; a finish with no coverage
 *    row covered nothing and adds no line; a completion whose job the device does not hold, or of another task type, is left out. Each line is *confirmed* with
 *    the confirmation of its session (a crew of two: each person's own, for the areas he covered), else *as planned* with its job's planned words;
 *  - **one reading of the words**: a confirmed word that is still a choice of the type is read through the type's CURRENT pictures (so a picture corrected in
 *    Types de travail corrects the confirmed and the planned history alike); the confirmation's stored `looks` serve only a word that is no longer a choice. A
 *    word with no picture is left out of the line and counted (`ignored`) — only for a setting some choice of which has a picture;
 *  - **planned** lines: a job planned, in progress or paused, of the same task type, scheduled from today up to the day BEFORE `until`, counts as planned (its
 *    words) on each of its areas that no completion of it covers yet; never as done; a cancelled job's plan never counts. A caller that proposes in place of
 *    some jobs (a series' week) lists them in `excludePlannedJobs`;
 *  - the same day twice on one area keeps ONE line: a done cut over a planned one, then the full coverage over a partial one, then the later finish, then the
 *    later group, then the job's id;
 *  - **who** (rule 2): only where the viewer holds `job.plan` AND `worklog.read` (a crew phone holds its own finishes alone, the workstation none): with
 *    `readsWork` false the history is empty and says it is gated, so that no device reads other people's work as planned. */
export const SETTINGS_HISTORY_DAYS = 90

export type HistoryKind = 'done' | 'planned'
export type HistoryBasis = 'confirmed' | 'as_planned' | 'planned'
export interface HistoryLine {
  areaId: string
  /** the site-local day of the cut */
  day: string
  kind: HistoryKind
  basis: HistoryBasis
  jobId: string
  /** the job's sequence group (the order of two planned jobs of one day) */
  group: number
  /** a done line: the finish's instant; a planned one: '' */
  at: string
  /** per choice setting the library picture of the word on this cut; a setting whose word has no picture is absent */
  codes: Readonly<Record<string, SettingLookCode>>
  /** per choice setting the club's word as it was typed (the texts show it) */
  words: Readonly<Record<string, string>>
}
export interface History {
  lines: HistoryLine[]
  /** the first day read (today − 90) and the first day NOT read (the target) */
  from: string
  until: string
  /** true when the viewer may not read others' work: nothing was read */
  gated: boolean
  counts: {
    /** done cuts read (an area on a day) */
    done: number
    /** of which confirmed at the finish */
    confirmed: number
    asPlanned: number
    /** planned cuts read */
    planned: number
    /** words with no picture, left out */
    ignored: number
    /** the earliest done day read, or null */
    firstDone: string | null
  }
}

export interface HistoryInput {
  tt: TaskTypeRow | undefined
  jobs: ReadonlyArray<Pick<JobRow, 'id' | 'task_type_code' | 'params' | 'state' | 'scheduled_local_date' | 'sequence_group'>>
  jobAreas: ReadonlyArray<Pick<JobAreaRow, 'job_id' | 'area_id' | 'removed_by_command_id'>>
  completions: ReadonlyArray<Pick<JobCompletionRow, 'id' | 'job_id' | 'work_session_id' | 'completed_at'>>
  coverage: ReadonlyArray<Pick<CoverageRow, 'completion_id' | 'area_id' | 'coverage'>>
  confirmations: ReadonlyArray<Pick<SettingsConfirmationRow, 'work_session_id' | 'values' | 'looks' | 'confirmed_at'>>
  /** the areas whose cuts are read */
  areaIds: Iterable<string>
  /** the site-local today, and the first day NOT read (the day the proposal is for) */
  today: string
  until: string
  tz: string
  readsWork: boolean
  excludePlannedJobs?: ReadonlySet<string>
  days?: number
}

const LIVE_PLANNED: ReadonlySet<string> = new Set(['planned', 'in_progress', 'paused'])
const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const EMPTY = (from: string, until: string, gated: boolean): History => ({ lines: [], from, until, gated, counts: { done: 0, confirmed: 0, asPlanned: 0, planned: 0, ignored: 0, firstDone: null } })

export function buildHistory(input: HistoryInput): History {
  const days = input.days ?? SETTINGS_HISTORY_DAYS
  const from = addDays(input.today, -days)
  const { tt, until } = input
  if (!input.readsWork) return EMPTY(from, until, true)
  if (!tt) return EMPTY(from, until, false)
  const areas = new Set(input.areaIds)
  // the choice settings the rule can read: some choice of which has a picture now
  const pictured = tt.param_defs.filter((d) => d.kind === 'choice' && (d.choices ?? []).some((c) => lookForChoice(tt, d.key, c) !== null)).map((d) => d.key)
  const isChoice = (key: string, word: string): boolean => !!tt.param_defs.find((d) => d.key === key)?.choices?.includes(word)
  const codesOf = (values: Readonly<Record<string, string | number>>, stored?: Readonly<Record<string, string | null>>): { codes: Record<string, SettingLookCode>; words: Record<string, string>; ign: number } => {
    const codes: Record<string, SettingLookCode> = {}, words: Record<string, string> = {}
    let ign = 0
    for (const key of pictured) {
      if (!Object.hasOwn(values, key)) continue
      const word = values[key]
      if (typeof word !== 'string') continue
      words[key] = word
      let code: SettingLookCode | null = lookForChoice(tt, key, word)
      if (code === null && stored && !isChoice(key, word) && Object.hasOwn(stored, key)) { const s = stored[key]; code = isSettingLookCode(s) ? s : null }
      if (code === null) ign++
      else codes[key] = code
    }
    return { codes, words, ign }
  }

  const jobById = new Map(input.jobs.map((j) => [j.id, j]))
  const confirmationOf = new Map<string, HistoryInput['confirmations'][number]>()
  for (const c of input.confirmations) { const was = confirmationOf.get(c.work_session_id); if (!was || c.confirmed_at > was.confirmed_at) confirmationOf.set(c.work_session_id, c) }

  // done lines, one per (completion, area)
  const levelOf = new Map<string, Map<string, 'full' | 'partial'>>()                      // completion → area → level (full wins)
  for (const c of input.coverage) {
    if (!areas.has(c.area_id)) continue
    const m = levelOf.get(c.completion_id) ?? new Map<string, 'full' | 'partial'>()
    if (c.coverage === 'full' || !m.has(c.area_id)) m.set(c.area_id, c.coverage)
    levelOf.set(c.completion_id, m)
  }
  const coveredByJob = new Set<string>()                                                    // `${job}:${area}`: an area one of the job's completions covers (in any coverage, any date)
  for (const c of input.completions) { const m = levelOf.get(c.id); if (m) for (const a of m.keys()) coveredByJob.add(`${c.job_id}:${a}`) }
  const cand: Array<HistoryLine & { full: boolean; ign: number }> = []
  for (const c of input.completions) {
    const m = levelOf.get(c.id)
    if (!m) continue
    const job = jobById.get(c.job_id)
    if (!job || job.task_type_code !== tt.code) continue
    const d = localDate(c.completed_at, input.tz)
    if (d < from || d >= until) continue
    const conf = confirmationOf.get(c.work_session_id)
    const { codes, words, ign } = conf ? codesOf(conf.values, conf.looks) : codesOf(job.params)
    for (const [areaId, level] of m) cand.push({ areaId, day: d, kind: 'done', basis: conf ? 'confirmed' : 'as_planned', jobId: job.id, group: job.sequence_group, at: c.completed_at, codes, words, full: level === 'full', ign })
  }
  // planned lines
  const areasOfJob = new Map<string, string[]>()
  for (const a of input.jobAreas) if (!a.removed_by_command_id && areas.has(a.area_id)) areasOfJob.set(a.job_id, [...(areasOfJob.get(a.job_id) ?? []), a.area_id])
  for (const job of input.jobs) {
    if (job.task_type_code !== tt.code || !LIVE_PLANNED.has(job.state) || input.excludePlannedJobs?.has(job.id)) continue
    if (job.scheduled_local_date < input.today || job.scheduled_local_date >= until) continue
    const { codes, words, ign } = codesOf(job.params)
    for (const areaId of new Set(areasOfJob.get(job.id) ?? [])) {
      if (coveredByJob.has(`${job.id}:${areaId}`)) continue
      cand.push({ areaId, day: job.scheduled_local_date, kind: 'planned', basis: 'planned', jobId: job.id, group: job.sequence_group, at: '', codes, words, full: false, ign })
    }
  }
  // one line per (area, day): done over planned, the full coverage over a partial one, the later finish, the later group, then the id
  type Cand = HistoryLine & { full: boolean; ign: number }
  const better = (a: Cand, b: Cand): boolean => {
    if (a.kind !== b.kind) return a.kind === 'done'
    if (a.full !== b.full) return a.full
    if (a.at !== b.at) return a.at > b.at
    if (a.group !== b.group) return a.group > b.group
    return a.jobId > b.jobId
  }
  const kept = new Map<string, Cand>()
  for (const l of cand) { const k = `${l.areaId}\u0000${l.day}`; const was = kept.get(k); if (!was || better(l, was)) kept.set(k, l) }
  const ignored = [...kept.values()].reduce((n, l) => n + l.ign, 0)
  const lines = [...kept.values()].map(({ full: _full, ign: _ign, ...l }): HistoryLine => l).sort((a, b) => cmp(a.day, b.day) || cmp(a.areaId, b.areaId) || cmp(a.jobId, b.jobId))
  const done = lines.filter((l) => l.kind === 'done')
  return {
    lines, from, until, gated: false,
    counts: {
      done: done.length, confirmed: done.filter((l) => l.basis === 'confirmed').length, asPlanned: done.filter((l) => l.basis === 'as_planned').length,
      planned: lines.length - done.length, ignored, firstDone: done.length ? done.reduce((m, l) => (l.day < m ? l.day : m), done[0]!.day) : null,
    },
  }
}

/** One job of a series on the tracking page (`/plan/settings`): what its day's stack shows. Three marks that never look alike: **confirmed** (finished, with the
 *  confirmation of a finish: the picture and word as confirmed), **unconfirmed** (finished, no confirmation read: shown as its planned values, said so),
 *  **planned** (not finished yet); `differs` is a confirmation that says another word than the plan's on a setting the plan had. A cancelled job has none. Only
 *  the settings the rule can read (some choice with a picture), in the order the type declares them; a word with no picture shows no picture. Pure. */
export interface TrackStack {
  jobId: string
  day: string
  mark: 'confirmed' | 'unconfirmed' | 'planned'
  differs: boolean
  /** per pictured setting, in the declared order: its key, the club's word and its picture (null: none) */
  items: Array<{ key: string; word: string; code: SettingLookCode | null }>
}
export function trackStack(
  job: Pick<JobRow, 'id' | 'task_type_code' | 'params' | 'state' | 'scheduled_local_date'>, tt: TaskTypeRow | undefined,
  completions: ReadonlyArray<Pick<JobCompletionRow, 'id' | 'job_id' | 'work_session_id'>>, confirmations: ReadonlyArray<Pick<SettingsConfirmationRow, 'work_session_id' | 'values' | 'looks' | 'confirmed_at'>>,
): TrackStack | null {
  if (!tt || job.state === 'cancelled') return null
  const finished = job.state === 'done' || job.state === 'partially_done'
  const sessions = new Set(completions.filter((c) => c.job_id === job.id).map((c) => c.work_session_id))
  const conf = finished ? confirmations.filter((c) => sessions.has(c.work_session_id)).sort((a, b) => (a.confirmed_at < b.confirmed_at ? 1 : a.confirmed_at > b.confirmed_at ? -1 : 0))[0] : undefined
  const mark: TrackStack['mark'] = !finished ? 'planned' : conf ? 'confirmed' : 'unconfirmed'
  const items: TrackStack['items'] = []
  let differs = false
  for (const d of tt.param_defs) {
    if (d.kind !== 'choice' || !(d.choices ?? []).some((c) => lookForChoice(tt, d.key, c) !== null)) continue
    const source = conf ? conf.values : job.params
    if (!Object.hasOwn(source, d.key)) continue
    const word = source[d.key]
    if (typeof word !== 'string') continue
    let code = lookForChoice(tt, d.key, word)
    if (code === null && conf && !(d.choices ?? []).includes(word) && Object.hasOwn(conf.looks, d.key)) { const s = conf.looks[d.key]; code = isSettingLookCode(s) ? s : null }
    items.push({ key: d.key, word, code })
    if (conf && Object.hasOwn(job.params, d.key) && job.params[d.key] !== word) differs = true
  }
  return { jobId: job.id, day: job.scheduled_local_date, mark, differs, items }
}
